import React, { useState, useCallback } from "react";
import LevelSidebar from "./components/LevelSidebar";
import LevelEditor from "./components/LevelEditor";
import { useLevels } from "./hooks/useLevels";
import { levelDefaults } from "../../../models/level";
import { ConfirmDialog, MasterDetailLayout, Breadcrumb } from "../../../components/Common";

/**
 * Levels Page (Módulo de Níveis)
 * Layout Master-Detail: Listagem lateral com drag & drop e editor.
 */
const LevelsPage = () => {
    const { levels, loading, isSaving, actions } = useLevels();
    const [selectedLevel, setSelectedLevel] = useState(levelDefaults);
    const [isDirty, setIsDirty] = useState(false);

    const handleSelectLevel = useCallback(async (level) => {
        if (selectedLevel?.id === level.id) return;

        // Só mostra o alerta se houver sujeira REAL (algo foi digitado e não é vazio)
        const isActuallyDirty = isDirty && (selectedLevel?.id || selectedLevel?.name?.trim() !== "" || false);

        if (isActuallyDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Você tem alterações pendentes neste nível. Deseja descartá-las?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar"
            });
            if (!confirmed) return;
        }

        setSelectedLevel(level);
        setIsDirty(false);
    }, [selectedLevel, isDirty]);

    const handleCreateNew = useCallback(async () => {
        const isActuallyDirty = isDirty && (selectedLevel?.id || selectedLevel?.name?.trim() !== "" || false);

        if (isActuallyDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Deseja descartar as mudanças atuais e criar um novo nível?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar"
            });
            if (!confirmed) return;
        }
        setSelectedLevel(levelDefaults);
        setIsDirty(false);
    }, [isDirty, selectedLevel]);

    const handleSave = useCallback(async (data) => {
        const success = await actions.saveLevel(data);
        if (success) {
            setIsDirty(false);
        }
    }, [actions]);

    const handleDelete = useCallback(async (id) => {
        const success = await actions.deleteLevel(id);
        if (success) {
            setSelectedLevel(levelDefaults);
            setIsDirty(false);
        }
    }, [actions]);

    const handleMarkDirty = useCallback((dirty) => setIsDirty(dirty), []);

    // PageLoader logic removido para carregamento nativo do sidebar/detalhe

    return (
        <React.Fragment>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <Breadcrumb title="Níveis" />
            </div>

            <div className="border-top mb-3"></div>

            <div className="levels-container mt-3">
                <MasterDetailLayout
                master={
                    <LevelSidebar
                        levels={levels}
                        activeId={selectedLevel?.id}
                        onSelect={handleSelectLevel}
                        onCreateNew={handleCreateNew}
                        loading={loading}
                        onReorder={actions.reorderLevels}
                    />
                }
                detail={
                    <LevelEditor
                        level={selectedLevel}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        isSaving={isSaving}
                        onChange={handleMarkDirty}
                    />
                }
            />
            </div>
        </React.Fragment>
    );
};

export default LevelsPage;
